/**
 * Fil d'Ariane du drill-down : « Tout › France › Alsace ».
 *
 * Chaque niveau au-dessus du niveau courant est cliquable et fait SAUTER
 * directement à ce niveau (`onDrillTo(depth)`, cf. `useDrillDown`) : à
 * profondeur ≥ 2, une remontée d'un cran ne ramènerait pas à « Tout ».
 * `depth` = nombre de niveaux conservés (0 = tout réinitialiser). Le dernier
 * crumb est le niveau affiché : il n'est pas cliquable.
 */
import { dimensionLabel, type FieldCatalog } from './fieldCatalog';
import type { DrillStep } from './useDrillDown';

const COLOR_MUTED = '#8B8FA7';
const COLOR_STRONG = '#1B2559';
const COLOR_ACCENT = '#6366F1';

export interface DrillBreadcrumbProps {
  drillPath: DrillStep[];
  catalog: FieldCatalog | null;
  /** Saute au niveau demandé (`depth` = nombre de niveaux conservés, 0 = « Tout »). */
  onDrillTo: (depth: number) => void;
}

export function DrillBreadcrumb({ drillPath, catalog, onDrillTo }: DrillBreadcrumbProps) {
  if (drillPath.length === 0) return null;

  const lastIndex = drillPath.length - 1;

  return (
    <nav
      aria-label="Fil d'Ariane du drill-down"
      className="mb-2 flex flex-wrap items-center gap-1 text-xs"
      style={{ color: COLOR_MUTED }}
    >
      <button
        type="button"
        onClick={() => onDrillTo(0)}
        className="rounded px-1 py-0.5 font-medium transition-colors hover:underline"
        style={{ color: COLOR_ACCENT }}
      >
        Tout
      </button>

      {drillPath.map((step, index) => {
        const isCurrent = index === lastIndex;
        const label = dimensionLabel(catalog, step.dimension);

        return (
          <span key={`${step.dimension}-${step.value}`} className="flex items-center gap-1">
            <span aria-hidden="true">›</span>
            {isCurrent ? (
              <span
                title={label}
                aria-current="page"
                className="px-1 py-0.5 font-semibold"
                style={{ color: COLOR_STRONG }}
              >
                {step.value}
              </span>
            ) : (
              <button
                type="button"
                title={label}
                // On conserve les niveaux jusqu'à celui-ci inclus → index + 1.
                onClick={() => onDrillTo(index + 1)}
                className="rounded px-1 py-0.5 font-medium transition-colors hover:underline"
                style={{ color: COLOR_ACCENT }}
              >
                {step.value}
              </button>
            )}
          </span>
        );
      })}
    </nav>
  );
}

export default DrillBreadcrumb;
